import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { ChartComponentProps, getBaseOption, formatTooltipValue, ECHARTS_OPTS } from './chartConfig';

export const BoxplotChart: React.FC<ChartComponentProps> = ({ chart, height = 320, colors }) => {
  const option = useMemo((): EChartsOption => {
    const data = chart.data || [];
    if (data.length === 0) return {};

    const base = getBaseOption(colors);

    const categories = data.map(d => String(d.name ?? ''));
    // Each datum should have [min, Q1, median, Q3, max] or individual fields
    const boxData = data.map(d => {
      if (Array.isArray(d.value)) return d.value;
      return [
        Number(d.min) || 0,
        Number(d.q1) || 0,
        Number(d.median ?? d.value) || 0,
        Number(d.q3) || 0,
        Number(d.max) || 0,
      ];
    });

    return {
      ...base,
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#7B6B72' },
        splitLine: { lineStyle: { color: '#EDE5D8', type: 'dashed' } },
      },
      tooltip: {
        ...(base.tooltip as object),
        trigger: 'item',
        formatter: (params: unknown) => {
          const p = params as { name?: string; value?: number[] };
          const v = p.value ?? [];
          return `<div style="font-size:12px"><b>${p.name}</b><br/>Min: ${formatTooltipValue(v[0])}<br/>Q1: ${formatTooltipValue(v[1])}<br/>Median: ${formatTooltipValue(v[2])}<br/>Q3: ${formatTooltipValue(v[3])}<br/>Max: ${formatTooltipValue(v[4])}</div>`;
        },
      },
      series: [{ type: 'boxplot', data: boxData }],
      legend: { show: false },
    };
  }, [chart, colors]);

  return <ReactECharts option={option} style={{ height }} notMerge lazyUpdate opts={ECHARTS_OPTS} />;
};
